<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Under_the_Hood';
    let nextLink= '/Documentation/Under_the_Hood/Editor_Framework/Predefined_Boxes';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="the-projectional-editor-framework-1"
  bind:intersecting={visibleById["the-projectional-editor-framework-1"]}
  onIntersectChange={(visible) => setVisible("the-projectional-editor-framework-1", visible)}
>The Projectional Editor Framework</SectionComponent>
<p>The Projectional Editor Framework is written in <a href="https://www.typescriptlang.org/" target="_blank">TypeScript</a>
and <a href="https://svelte.dev/" target="_blank">Svelte</a>. This page provides an overview of the framework.<br>
If you want to know how to customize it, see <a
  href="/Documentation/Customizations/Editor_Customization"
>Editor Customization</a>.</p>
<SectionComponent tag="h2" 
  id="the-ast-the-box-tree-and-the-dom-2"
  bind:intersecting={visibleById["the-ast-the-box-tree-and-the-dom-2"]}
  onIntersectChange={(visible) => setVisible("the-ast-the-box-tree-and-the-dom-2", visible)}
>The AST, the Box Tree, and the DOM</SectionComponent>
<p>The internal representation of any model written in a DSL is a tree of instances of TypeScript classes —<br>
the <strong>Abstract Syntax Tree (AST)</strong>. All AST classes implement the core interface <a
  href="/Documentation/Under_the_Hood/Core_Interfaces#frenode-2"
><code>FreNode</code></a>.</p>
<p>Because the visual representation in the editor can differ from the AST, every visual element is represented by another TypeScript class called a <strong>Box</strong>.<br>
All boxes used to render a certain projection set or editor view form another tree called the <strong>Box Tree</strong>.<br>
In fact, any box tree is the internal representation of the concrete syntax, and can therefore also be called the <strong>Concrete Syntax Tree (CST)</strong>.</p>
<p>There can be multiple box trees for one AST, because there can be multiple projection sets (or editors, or views — whichever term you prefer).<br>
Thus, at any given moment there are two internal TypeScript representations of the model:</p>
<ul>
<li>The <strong>Abstract Syntax Tree (AST)</strong></li>
<li>The <strong>Concrete Syntax Tree (CST)</strong>, or <strong>Box Tree</strong></li>
</ul>
<p>Because Freon produces a web-based editor, there is yet another representation of the content: the <strong>Document Object Model (DOM)</strong>.<br>
According to <a href="https://developer.mozilla.org/en-US/docs/Web/API/Document_Object_Model/Introduction" target="_blank">MDN Web Docs</a>, the DOM is the data representation of the objects that comprise the structure and content of a document on the web.</p>
<p>In Freon, DOM elements are implemented as <a href="https://svelte.dev/" target="_blank">Svelte</a> components.<br>
Whenever the Freon editor is in use, it keeps these three different representations of the user’s model in sync.</p>
<p>This section of the documentation explains the <strong>Box Tree</strong>.</p>
<SectionComponent tag="h2" 
  id="projection-layout-based-on-boxes-3"
  bind:intersecting={visibleById["projection-layout-based-on-boxes-3"]}
  onIntersectChange={(visible) => setVisible("projection-layout-based-on-boxes-3", visible)}
>Projection Layout Based on Boxes</SectionComponent>
<p>The layout of every visible element in the editor is based on <em>boxes</em>. A box is a rectangular area
in the editor. Every AST node is projected in a box, and a box can contain any number of child boxes.</p>
<p>The framework provides many predefined box types. See<br>
<a
  href="/Documentation/Under_the_Hood/Editor_Framework/Predefined_Boxes"
>Predefined Boxes</a> for an overview.<br>
These boxes can be combined to build a projection.</p>
<p>Let’s look at a simple example. Figure 1 shows the projection of the concept <code>StringLiteral</code>.<br>
This is mapped to a horizontal list consisting of:</p>
<ul>
<li>an opening quote (<code>"</code>),</li>
<li>an editable text box showing the value of the <code>StringLiteral</code>,</li>
<li>and a closing quote (<code>"</code>).</li>
</ul>
<p>Note that the quotes are not part of the AST; they belong to the concrete syntax used in this projection.</p>
<Figure
imageName='documentation/mapping-example-stringliteral.svg'
caption='Mapping a StringLiteral'
figureNumber={1}
/>
<p>In TypeScript, using the <a
  href="/Documentation/Under_the_Hood/Editor_Framework/Predefined_Boxes"
>predefined boxes</a>, this projection might look like this:</p>
<pre class="language-ts"><code class="language-ts">new HorizontalListBox([
    new LabelBox('"'),
    new TextBox(stringLiteral.value),
    new LabelBox('"')
]);</code></pre>
<SectionComponent tag="h2" 
  id="coupling-behavior-to-boxes-4"
  bind:intersecting={visibleById["coupling-behavior-to-boxes-4"]}
  onIntersectChange={(visible) => setVisible("coupling-behavior-to-boxes-4", visible)}
>Coupling Behavior to Boxes</SectionComponent>
<p>User actions on boxes need to be translated into interactions with the projected AST.<br>
To know which part of the projection represents which AST node, we give each <code>Box</code> in the projection a <strong>role</strong>, which is unique within its parent box.<br>
This allows every box to be identified by its model element plus its role.</p>
<p>In the following figure, the roles are shown above the boxes in green.</p>
<Figure
caption='Mapping a StringLiteral with Roles'
imageName='documentation/mapping-example-stringliteral-with-roles.svg'
figureNumber={2}
/>
<p>We can now show the full TypeScript code for this projection.<br>
Assume that <code>literal</code> is the <code>StringLiteral</code> object:</p>
<pre class="language-ts"><code class="language-ts">
new HorizontalListBox(literal, "full-string", [      // &lt;1&gt;
    new LabelBox(literal, "start-quote", '"'),       // &lt;2&gt;
    new TextBox(                                     // &lt;3&gt;
        literal,
        "value",
        () =&gt; literal.value,
        (v: string) =&gt; (literal.value = v)
    ),
    new LabelBox(literal, "end-quote", '"')          // &lt;4&gt;
]);</code></pre>
<ol>
<li>The overall horizontal list box that groups everything.<br>
Parameters: the model element shown, the role of the box, and a list of child boxes.</li>
<li>The first label box for the opening quote.<br>
Parameters: the model element, the role of the box, and its content (the quote character).</li>
<li>The text box for the value of the string literal.<br>
Parameters: the model element, the role of the box, a getter function for the content, and a setter function for when the content changes.</li>
<li>The second label box for the closing quote.<br>
Except for its role, it is identical to the first label box.</li>
</ol>
<SectionComponent tag="h2" 
  id="behavior-is-defined-by-actions-5"
  bind:intersecting={visibleById["behavior-is-defined-by-actions-5"]}
  onIntersectChange={(visible) => setVisible("behavior-is-defined-by-actions-5", visible)}
>Behavior Is Defined by Actions</SectionComponent>
<p>The behavior coupled to a <em>Box</em> is defined by an <strong>Action</strong>.<br>
To identify which action should be executed, each behavior specifies:</p>
<ul>
<li>a <strong>trigger</strong>, describing the key(s) that activate the behavior,</li>
<li>one or more <strong>roles</strong>, specifying that the behavior is only active in boxes with those roles,</li>
<li>an <strong>action</strong>, which is a TypeScript/JavaScript function executed when the behavior is triggered,</li>
<li>an optional <strong>caretPosition</strong>, specifying where the cursor should be placed after execution.</li>
</ul>
<p>There are four main types of action, all extending the <code>FreBehavior</code> interface:</p>
<ol>
<li><code>FreCustomBehavior</code> — used for most types of behavior, especially creation of non-expression concepts.</li>
<li><code>FreExpressionCreator</code> — used to create an expression.</li>
<li><code>FreBinaryExpressionCreator</code> — used to create a binary expression.</li>
<li><code>KeyboardShortcutBehavior</code> — used for behaviors initiated through a keystroke.</li>
</ol>
<pre class="language-ts"><code class="language-ts">export interface FreBehavior &#123;
    /** The trigger to activate this behavior */
    trigger: FreTriggerType;
    /** The box roles in which this trigger is active */
    activeInBoxRoles: string[];
    /** Optional callback function to determine whether the trigger applies for the specific box */
    isApplicable?: (box: Box) =&gt; boolean;
    boxRoleToSelect?: string;
    caretPosition?: FreCaret;
    referenceShortcut?: ReferenceShortcut;
&#125;

/** Behavior with a custom action, intended for non-expression elements */
export interface FreCustomBehavior extends FreBehavior &#123;
	action: (box: Box, trigger: string, editor: FreEditor, propertyName?: string) =&gt; FreNode | null;
	undo?: (box: Box, ed: FreEditor) =&gt; void;
&#125;

/** Special behavior for creating an expression */
export interface FreExpressionCreator extends FreBehavior &#123;
	expressionBuilder: (box: Box, trigger: string, editor: FreEditor, propertyName?: string) =&gt; FreExpression;
&#125;

/** Special behavior for creating a binary expression */
export interface FreBinaryExpressionCreator extends FreBehavior &#123;
	expressionBuilder: (box: Box, trigger: string, editor: FreEditor, propertyName?: string) =&gt; FreBinaryExpression;
&#125;

/** Special behavior initiated by keyboard strokes */
export interface KeyboardShortcutBehavior extends FreBehavior &#123;
	action: (box: Box, trigger: FreKey, editor: FreEditor, propertyName?: string) =&gt; Promise&lt;FreNode&gt;;
	trigger: FreKey;
&#125;</code></pre>
<p>To attach a behavior to one or more boxes, add those box roles to the <code>activeInBoxRoles</code> property of the action.</p>


<PrevNextSection {prevLink} {nextLink} />